import { baseApi } from '../../../core/baseApi';
import { ArchiveRequest, createRestoreMutation } from '../../../patterns/archive';
import { reduxLogger } from '@/core/utils/logger';
import { conversationQueriesApi } from './queries';
import type { ConversationData, GetConversationsParams } from '../types';

/**
 * Optimistically remove a conversation from all conversation list caches.
 * Uses the same getState/dispatch pattern as toggleConversationStatus.
 * Returns patch results so the caller can undo on failure.
 */
function removeConversationFromCaches(
  id: string,
  dispatch: (...args: unknown[]) => unknown,
  getState: () => unknown
) {
  const patchResults: Array<{ undo: () => void }> = [];

  const applyPatch = (endpointName: string, args: unknown) =>
    dispatch(
      conversationQueriesApi.util.updateQueryData(endpointName as 'getConversations', args as GetConversationsParams | undefined, (draft) => {
        if (!draft || !('items' in draft)) return;
        const idx = draft.items.findIndex((c: ConversationData) => c.id === id);
        if (idx !== -1) {
          draft.items.splice(idx, 1);
          if (typeof draft.total === 'number') draft.total--;
        }
      })
    ) as unknown as { undo: () => void };

  try {
    const state = getState() as { api?: { queries?: Record<string, { endpointName?: string; originalArgs?: unknown }> } };
    const queries = state?.api?.queries || {};
    const seen = new Set<string>();
    Object.entries(queries).forEach(([key, q]) => {
      if (!q || seen.has(key)) return;
      if (q.endpointName === 'getConversations' || q.endpointName === 'getInfiniteConversations') {
        seen.add(key);
        patchResults.push(applyPatch(q.endpointName, q.originalArgs));
      }
    });
  } catch { /* noop */ }

  return patchResults;
}

export const conversationArchiveApi = baseApi.injectEndpoints({
  // Allow overriding because the new unified archive slice already registers some of these names
  overrideExisting: true,
  endpoints: (builder) => ({
    archiveConversation: builder.mutation<void, ArchiveRequest>({
      query: ({ id, reason }: ArchiveRequest) => ({
        url: `/conversations/${id}/toggle-archive`,
        method: 'POST',
        body: { isArchived: true, reason }
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        // True optimistic update: directly remove conversation from all caches
        const patches = removeConversationFromCaches(id, dispatch, getState);

        // Toast immediately with optimistic update — don't wait for server
        import('@/shared/utils/toast').then(({ showToast }) =>
          showToast.success('Conversation archived'));

        try {
          await queryFulfilled;
          // On success, refresh archived list so it appears there
          dispatch(
            baseApi.util.invalidateTags([
              { type: 'ArchivedConversations', id: 'ARCHIVED-LIST' }
            ])
          );
        } catch (error) {
          reduxLogger.error('Archive operation failed, rolling back:', error);
          import('@/shared/utils/toast').then(({ showToast }) =>
            showToast.error('Failed to archive conversation'));
          // On failure, undo the optimistic removal
          patches.forEach((p) => p.undo());
        }
      }
    }),
    restoreConversation: createRestoreMutation('/conversations', 'Conversations', 'ArchivedConversations')(builder),
  })
});

export const {
  useArchiveConversationMutation,
  useRestoreConversationMutation,
} = conversationArchiveApi;
